import { action, computed, flow, makeObservable, observable } from 'mobx';
import { persist } from 'mobx-persist';
import usersStore from './usersStore';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import { LoadingStatus, TBooleanInNumber } from 'src/types/main';
import { IRole } from 'src/types/role';
import { booleanNumberReverse } from 'src/utils/helpers';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';

class RolesStore {
  constructor() {
    makeObservable(this);
  }

  hideRoles: string[] = [];
  getRolesAbortController = new AbortController();
  rolesGetTimeoutId: NodeJS.Timeout | null = setTimeout(() => {
    return;
  }, 0);

  @observable public rolesLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public rolePermissionLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @persist @observable roles: IRole[] = [];
  @persist @observable role: IRole | null = null;

  setHideRoles(roles: string[]) {
    this.hideRoles = roles;
  }

  private _getRoles = flow(function* (this: RolesStore): any {
    this.getRolesAbortController.abort();
    this.getRolesAbortController = getAbortController();

    try {
      this.rolesLoadingStatus = LoadingStatus.Loading;

      const response = yield client.get('v4/roles', { signal: this.getRolesAbortController.signal });

      this.roles = response.data;
      if (this.role === null) this.role = this.roles[0];
      this.rolesLoadingStatus = LoadingStatus.Success;
      usersStore.selectAllRoles();
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.rolesLoadingStatus = LoadingStatus.Success;
        return;
      }

      this.rolesLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'RolesStore _getRoles', showModal: true });
    }
  }).bind(this);

  @computed get selectRolesOptions(): IPickerOption[] {
    let filteredRoles = this.roles;

    if (this.hideRoles.length > 0) {
      filteredRoles = this.roles.filter((role) => !this.hideRoles.includes(role.name));
    }

    return filteredRoles.map((role) => ({
      label: localeStore.t(`${role.name}_role`),
      value: role.id,
      data: {
        name: role.name,
      },
    }));
  }

  @action getRoles = async () => {
    await this._getRolesWithNoDelay();
  };

  @action private _getRolesWithNoDelay = async () => {
    if (this.rolesGetTimeoutId) {
      clearTimeout(this.rolesGetTimeoutId);
    }
    await this._getRoles();
  };

  @action private _getRolesWithDelay = () => {
    if (this.rolesGetTimeoutId) {
      clearTimeout(this.rolesGetTimeoutId);
    }
    this.rolesGetTimeoutId = setTimeout(() => {
      this._getRoles();
    }, 700);
  };

  @action setActiveRole = (roleId: number) => {
    let index = this.roles.findIndex((role) => role.id === roleId);
    if (index === -1) {
      index = 0;
    }
    this.role = this.roles[index];
  };

  @action updatePermissionAvailability = flow(function* (
    this: RolesStore,
    roleId: number | undefined,
    permissionId: number,
    isAvailable: TBooleanInNumber
  ) {
    if (!roleId) return;

    try {
      this.rolePermissionLoadingStatus = LoadingStatus.Loading;

      this.roles.map((role) => {
        if (role.id !== roleId) return role;

        return role.permissions.map((permission) => {
          if (permission.id !== permissionId) return permission;
          permission.enabled = isAvailable;
          return permission;
        });
      });

      yield client.put(`v4/roles/${roleId}/permissions/${permissionId}`, { availability: isAvailable });
      this.rolePermissionLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.roles.map((role) => {
        this.rolePermissionLoadingStatus = LoadingStatus.Error;
        if (role.id !== roleId) return role;

        return role.permissions.map((permission) => {
          if (permission.id !== permissionId) return permission;
          permission.enabled = booleanNumberReverse(permission.enabled);
          return permission;
        });
      });

      logger.error({ error, place: 'RolesStore updatePermissionAvailability', showModal: true });
    }
  }).bind(this);
}

const rolesStore = new RolesStore();
export default rolesStore;
